<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue';
import { useRouter } from '#app/composables/router';
import { useEntitlement, useOrganizationEntitlement, usePermissionsStore } from '#layers/access/public';
import { customSpacesFlag } from '#layers/base/public';
import { resolveSettingsNavigation } from '#layers/settings/public';
import {
	ASSETS_ROUTE_NAME,
	CONNECT_ROUTE_NAME,
	DATA_MODEL_ROUTE_NAME,
	WORKSPACE_SETTINGS_ROUTE_NAME,
} from '#layers/spaces/public';
import { useActiveCollection } from '#layers/ui-workspace/composables/use-active-collection';
import { ScrollFadeAxis, useScrollFade } from '#layers/ui/public';
import SpacebarDivider from './Divider.vue';
import SpacebarItem from './Item.vue';
import SpacebarWorkspaceEntries from './WorkspaceEntries.vue';

interface Props {
	/** Keeps stable shell actions mounted while hiding entries derived from a workspace that is changing. */
	workspaceSwitching?: boolean;
}

const { workspaceSwitching = false } = defineProps<Props>();

const router = useRouter();
const { can } = useEntitlement();
const { can: canOrganization } = useOrganizationEntitlement();
const permissions = usePermissionsStore();
const { contentModeRoute, dataModelModeRoute, isContentActive, isDataModelActive } = useActiveCollection();

const routes = computed(() => router.getRoutes());

function isRouteVisible(routeName: string): boolean {
	const candidate = routes.value.find((record) => record.name === routeName);
	return !candidate?.meta.isVisible || candidate.meta.isVisible({ can, canOrganization });
}

const showDataModel = computed(() => isRouteVisible(DATA_MODEL_ROUTE_NAME));
const hasVisibleSettings = computed(() => resolveSettingsNavigation({ routes: routes.value, can, canOrganization }).groups.length > 0);

const scrollRef = useTemplateRef<HTMLElement>('scroll');
const topGroupRef = useTemplateRef<HTMLElement>('topGroup');
const bottomGroupRef = useTemplateRef<HTMLElement>('bottomGroup');

const { style: scrollFadeStyle } = useScrollFade(scrollRef, {
	axis: ScrollFadeAxis.Vertical,
	content: [topGroupRef, bottomGroupRef],
});
</script>

<template>
	<!-- `border-be border-transparent` aligns spacebar items with the content footer -->
	<nav
		ref="scroll"
		class="
			scrollbar-hidden flex min-h-0 w-full flex-1 flex-col items-stretch overflow-y-auto overscroll-none border-be
			border-transparent mask-t-from-(--scroll-fade-start) mask-b-from-(--scroll-fade-end) pbe-2
		"
		:style="scrollFadeStyle"
	>
		<div ref="topGroup" class="flex shrink-0 flex-col items-center gap-2 pbe-2 *:shrink-0">
			<SpacebarDivider />

			<SpacebarItem
				:route-name="contentModeRoute.name"
				:route-params="contentModeRoute.params"
				:label="$t('pages.content.title')"
				icon="lucide:box"
				:active="isContentActive"
			/>

			<SpacebarItem
				:route-name="ASSETS_ROUTE_NAME"
				:label="$t('common.assets')"
				icon="lucide:folder"
			/>

			<SpacebarWorkspaceEntries v-if="customSpacesFlag && !workspaceSwitching" />
		</div>

		<div
			ref="bottomGroup"
			class="mt-auto flex shrink-0 flex-col items-center gap-2 pbe-1"
		>
			<SpacebarDivider />

			<template v-if="!permissions.isReady">
				<div
					v-for="n in 3"
					:key="n"
					data-testid="spacebar-skeleton"
					class="size-9 rounded-md bg-elevated"
				/>
			</template>

			<template v-else>
				<SpacebarItem
					:route-name="CONNECT_ROUTE_NAME"
					:label="$t('pages.connect.title')"
					icon="lucide:plug"
				/>

				<SpacebarItem
					v-if="showDataModel"
					:route-name="dataModelModeRoute.name"
					:route-params="dataModelModeRoute.params"
					:label="$t('pages.data_model.title')"
					icon="lucide:database"
					:active="isDataModelActive"
				/>

				<SpacebarItem
					v-if="hasVisibleSettings"
					:route-name="WORKSPACE_SETTINGS_ROUTE_NAME"
					:label="$t('pages.settings.title')"
					icon="lucide:settings"
				/>
			</template>
			<slot name="user" />
		</div>
	</nav>
</template>
